import type { ProductCardTranslations } from '@/core/tamagoshi/ProductCard/interfaces';
import { formatCurrency } from '@/domains/core/localization/currency';
import { translate, translateFormatter } from '@/domains/core/localization/translate';
import { CURRENCY } from '@/domains/core/settings/constants';
import { isB2B, isB2C } from '@/domains/core/settings/utils';
import { SponsoredDetailedDescription, type SponsoredData } from '@/productDiscovery/ProductCard/components/SponsoredDetailedDescription';
import { getSponsoredLabelText } from '@/sellerAdsManagement/sponsoredLabelText/sponsoredLabelText';
import { SponsoredDescription } from './components/SponsoredDescription';
export const {
  addToCartLabel,
  addToCartNarrowLabel,
  addToCartSuccess,
  addToCartError,
  mention,
  mentionB2B,
  mentionNoVAT,
  priceVAT,
  priceWithoutVAT,
  inStockLabel,
  topSaleLabel,
  promoTag,
  bestQualityOffer,
  bulkTag,
  couponTag,
  freeDelivery,
  freeDeliveryByMM,
  freeFastDeliveryByMM,
  fastDelivery,
  fastDeliveryByMM,
  headerText,
  seeProduct,
  secondHandLabel,
  blackFridayLabel,
  salesLabel,
  manomanoDaysLabel,
  proDaysLabel,
  recommendedByPros,
  loweredPriceLabel
} = {
  ...translate({
    "mention": "Inkl. MwSt., zzgl. Versand",
    "mentionB2B": "Exkl. Versand",
    "mentionNoVAT": "0% MwST. unter Bedingungen",
    "freeDeliveryLine": "Free delivery",
    "priceVAT": "inc VAT",
    "priceWithoutVAT": "ex VAT",
    "inStockLabel": "In stock",
    "topSaleLabel": "Best sellers",
    "addToCartLabel": "Add to cart",
    "addToCartNarrowLabel": "Add",
    "addToCartSuccess": "Added",
    "addToCartError": "Error",
    "promoTag": "Deal",
    "bestQualityOffer": "The ManoMano selection",
    "bulkTag": "Volume discount",
    "couponTag": "offered",
    "freeDelivery": "Free delivery",
    "freeDeliveryByMM": "Free delivery 48h",
    "freeFastDeliveryByMM": "Free delivery 48h",
    "fastDelivery": "Delivery in 24 hours",
    "fastDeliveryByMM": "Delivery in 24 hours by",
    "headerText": "Buy again",
    "seeProduct": "See product",
    "secondHandLabel": "Seconde vie",
    "blackFridayLabel": "Black Friday",
    "salesLabel": "Sale",
    "manomanoDaysLabel": "ManoManoDays",
    "proDaysLabel": "ProDays",
    "recommendedByPros": "The professionals' choice",
    "loweredPriceLabel": "Price"
  })
};
export const proPriceAdvantageTag = translateFormatter<{
  proAdvantagePercentage: number;
}>("Pro Price -{proAdvantagePercentage}%", undefined, undefined, "en");
export const translations: ProductCardTranslations = {
  addToCart: {
    label: addToCartLabel,
    success: addToCartSuccess,
    error: addToCartError
  },
  informationLine: {
    alternativeModel: ''
  },
  legalMention: {
    mention: isB2B() ? mentionB2B : mention,
    mentionNoVAT
  },
  price: {
    VAT: priceVAT,
    withoutVAT: priceWithoutVAT
  },
  tags: {
    inStock: {
      label: inStockLabel
    },
    sponsored: {
      label: getSponsoredLabelText(),
      description: SponsoredDescription
    },
    topSale: {
      label: topSaleLabel
    },
    promo: {
      label: promoTag
    },
    bestQualityOffer: {
      label: bestQualityOffer
    },
    bulk: {
      label: bulkTag
    },
    coupon: {
      label: couponTag,
      labelShort: ''
    },
    secondHand: {
      label: secondHandLabel
    },
    blackFriday: {
      label: blackFridayLabel
    },
    sales: {
      label: salesLabel
    },
    manomanoDays: {
      label: isB2C() ? manomanoDaysLabel : proDaysLabel
    },
    recommendedByPros: {
      label: recommendedByPros
    },
    loweredPrice: {
      label: loweredPriceLabel
    }
  },
  deliveryLabel: {
    freeDelivery,
    fastDelivery,
    freeDeliveryByMM,
    freeFastDeliveryByMM,
    fastDeliveryByMM
  }
};
export const modelEnrichedTranslate = translateFormatter("{count} models available", undefined, undefined, "en");
const cache: {
  [key: string]: string;
} = {};
const formatAndCacheModelTranslation = (modelsCount: number): string => {
  cache[modelsCount] = modelEnrichedTranslate.format({
    count: modelsCount
  }).toString();
  return cache[modelsCount];
};
for (let i = 1; i < 11; i += 1) {
  cache[i] = formatAndCacheModelTranslation(i);
}
export const getProductCardTranslations = (modelsCount: number | undefined, couponAmount?: string | undefined, narrow = false, sponsoredData?: SponsoredData): ProductCardTranslations => {
  translations.informationLine.alternativeModel = modelsCount !== undefined ? cache[modelsCount] ?? formatAndCacheModelTranslation(modelsCount) : '';
  if (couponAmount && translations?.tags.coupon) {
    translations.tags.coupon.label = `${formatCurrency(couponAmount, CURRENCY, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    })} ${couponTag}`;
    translations.tags.coupon.labelShort = `-${formatCurrency(couponAmount, CURRENCY, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    })}`;
  }
  translations.addToCart.label = narrow ? addToCartNarrowLabel : addToCartLabel;
  if (sponsoredData && sponsoredData.adPayer) {
    translations.tags.sponsored.description = () => SponsoredDetailedDescription({
      sponsoredData
    });
  }
  return translations;
};